import '../../pagination/message/pagination-message';
import store from '../../../store/store';
import { CreateSearchAnalytics } from './create-search.analytics';
import { CreateSearchResultsView } from './create-search-results';
import { PaginationBase } from '../../pagination/pagination-base';
import { StoreState } from '../../../store/store.interface';
import { connect } from 'pwa-helpers';
import { customElement, property } from 'lit/decorators.js';
import { html, nothing, TemplateResult } from 'lit';
import { performCreateSearch } from './thunks/create-search.thunks';
import { selectCreateRequest, selectCreateSearchTotal } from './store/create-search.selector';
import { setCreateCurrentPageIndex, setCreateCurrentPageSize } from './store/create-search.store';

@customElement('mhecomm-create-search-pagination')
export class CreateSearchPagination extends connect(store)(PaginationBase) {
  /**
   * The number of pages to show in the pagination. This does not include the first and last pages or the arrows. It should be an odd number.
   */
  @property({ type: Number })
  maxPagesShown = 3;

  /**
   * The message to display to the user when there are results. The following placeholders are available: %first% %last% %total% %term%
   */
  @property()
  message = 'Showing %first%-%last% of %total% Results';

  /**
   * The store name for analytics events.
   */
  @property()
  store?: string = '';

  /**
   * Overriding base components unloadedHeight.
   */
  unloadedHeight = '58px';

  stateChanged(state: StoreState): void {
    const request = selectCreateRequest(state);
    const total = selectCreateSearchTotal(state) || 0;
    this.store = request?.store;
    let currentPage = 0;

    if (request && request.start >= request.pageSize) {
      currentPage = Math.floor((request.start - 1) / request.pageSize);
    }

    if (request && (this.total != total || this.currentPage != currentPage)) {
      this.total = total;
      this.currentPage = currentPage;
      this.pageLength = request.pageSize || 10;
      this.updatePages();
      store.dispatch(setCreateCurrentPageIndex(currentPage + 1));
      store.dispatch(setCreateCurrentPageSize(this.pageLength));
    }
  }

  renderComp(): TemplateResult | typeof nothing {
    return this.paginationComponent();
  }

  override showPaginationMsg(): TemplateResult {
    return html`
      <mhecomm-create-search-pagination-message
        data-automation-id=${`${this.id}-pagination_message`}
        id=${`${this.id}-pagination_message`}
        message="${this.message}"
      </mhecomm-create-search-pagination-message>
    `;
  }

  override goToPageHook(pageIndex: number): void {
    store.dispatch(
      performCreateSearch({
        start: pageIndex * this.pageLength + 1,
      })
    );

    (document.querySelector('mhecomm-create-search-results') as CreateSearchResultsView)?.scrollToResult();
    this.currentPage = pageIndex;
    const activePageId = `page-${pageIndex + 1}`;
    setTimeout(() => {
      this.shadowRoot?.getElementById(activePageId)?.focus();
    }, 100);
  }

  override emitAnalytics(pageIndex: number): void {
    CreateSearchAnalytics.goToPage({ element: this, id: this.id, pageIndex: pageIndex, storeCategory: this.store });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-create-search-pagination': CreateSearchPagination;
  }
}
